import type { UserCampaignSummary } from "@west-marches/shared";

const LOCAL_STORAGE_PREFIX = "west-marches:last-campaign";

function storageKey(userId: string): string {
  return `${LOCAL_STORAGE_PREFIX}:${userId}`;
}

function safeLocalStorage(): Storage | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function getLastOpenedShortId(userId: string | null | undefined): string | null {
  if (!userId) {
    return null;
  }
  const storage = safeLocalStorage();
  if (!storage) {
    return null;
  }
  try {
    const value = storage.getItem(storageKey(userId));
    return value && value.length > 0 ? value : null;
  } catch {
    return null;
  }
}

export function setLastOpenedShortId(
  userId: string | null | undefined,
  shortId: string | null,
): void {
  if (!userId) {
    return;
  }
  const storage = safeLocalStorage();
  if (!storage) {
    return;
  }
  try {
    if (!shortId) {
      storage.removeItem(storageKey(userId));
      return;
    }
    storage.setItem(storageKey(userId), shortId);
  } catch {
    // Swallow quota / privacy errors.
  }
}

export function clearLastOpenedShortId(userId: string | null | undefined): void {
  setLastOpenedShortId(userId, null);
}

/**
 * Pick the campaign a signed-in user should land on by default.
 *
 *   1. Last-opened (if it still exists in `campaigns`),
 *   2. else the most-recently-updated campaign,
 *   3. else `null`.
 */
export function resolveDefaultCampaignShortId(
  campaigns: readonly UserCampaignSummary[] | null | undefined,
  userId: string | null | undefined,
): string | null {
  if (!campaigns || campaigns.length === 0) {
    return null;
  }

  const lastOpened = getLastOpenedShortId(userId);
  if (lastOpened) {
    const stillExists = campaigns.some((campaign) => campaign.shortId === lastOpened);
    if (stillExists) {
      return lastOpened;
    }
  }

  const sorted = [...campaigns].sort((left, right) => {
    const leftUpdated = Date.parse(left.updatedAt);
    const rightUpdated = Date.parse(right.updatedAt);
    if (Number.isNaN(leftUpdated) || Number.isNaN(rightUpdated)) {
      return left.id.localeCompare(right.id);
    }
    return rightUpdated - leftUpdated;
  });

  return sorted[0]?.shortId ?? null;
}
